/**
 * Form controls. Field wraps a control in its <label> so the label names it
 * (only the label text sits inside, so getByLabelText and screen readers read
 * the same name); Input, Select and Checkbox share one control look.
 */

import { Icon } from "./Icon.jsx";

export const controlClass =
  "block w-full rounded-control border border-line-default bg-surface-1 text-ink transition-[border-color,box-shadow] duration-150 placeholder:text-ink-3 hover:border-line-strong focus:border-accent focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--accent)_16%,transparent)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60";

const SIZES = {
  sm: "h-9 px-3 text-[12px]",
  md: "h-10 px-3.5 text-[12.5px]",
  lg: "h-12 px-4 text-[14px]",
};

export function Field({ label, children, className = "" }) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="text-[11.5px] font-medium text-ink-2">{label}</span>
      {children}
    </label>
  );
}

/** `icon` sits inside the left edge; `trailing` (e.g. a show-password button) inside the right. */
export function Input({ size = "md", icon, trailing, className = "", ...rest }) {
  const input = <input className={`${controlClass} ${SIZES[size] ?? SIZES.md} ${icon ? "pl-9" : ""} ${trailing ? "pr-10" : ""} ${className}`} {...rest} />;
  if (!icon && !trailing) return input;
  return (
    <span className="relative block">
      {icon && <Icon name={icon} className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-3" />}
      {input}
      {trailing && <span className="absolute inset-y-0 right-1 flex items-center">{trailing}</span>}
    </span>
  );
}

export function Select({ size = "md", className = "", children, ...rest }) {
  return (
    <span className="relative block">
      <select className={`${controlClass} ${SIZES[size] ?? SIZES.md} appearance-none pr-8 ${className}`} {...rest}>
        {children}
      </select>
      <Icon name="chevronDown" className="pointer-events-none absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 text-ink-3" />
    </span>
  );
}

/** A checkbox with its label and an optional second line. */
export function Checkbox({ label, description, className = "", ...rest }) {
  return (
    <label className={`flex cursor-pointer gap-2 text-[12px] text-ink-2 ${description ? "items-start" : "items-center"} ${className}`}>
      <input type="checkbox" className={`size-3.5 shrink-0 cursor-pointer ${description ? "mt-0.5" : ""}`} {...rest} />
      <span>
        {label}
        {description && <span className="block text-[11px] text-ink-3">{description}</span>}
      </span>
    </label>
  );
}
